/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\RoleProfilePermissionsMatrix.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Role Profiles module.
----------------------------------------------------------------*/


import { motion } from 'framer-motion';
import { Search, Maximize2, Minimize2, Shield } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { renderIcon } from '../utils';

interface PermissionLevel {
    value: string;
    label: string;
    color: string;
}

interface RoleProfilePermissionsMatrixProps {
    loading: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    organizeMenuItems: any[];
    permissionLevels: PermissionLevel[];
    expandedSections: Set<string | number>;
    expandedGroups: Set<string | number>;
    searchQuery: string;
    onSearchChange: (v: string) => void;
    toggleSection: (id: string | number) => void;
    toggleGroup: (id: string | number) => void;
    handleToggleAll: () => void;
    areAllExpanded: () => boolean;
    getPermissionLevel: (id: string | number) => string;
    hasInheritedPermission: (id: string | number) => boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handlePermissionChange: (id: string | number, level: 'FULL' | 'READ' | 'NONE', setFormData: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFormData: (data: any) => void;
    t: (key: string, params?: Record<string, unknown>, fallback?: string) => string;
}

export function RoleProfilePermissionsMatrix({
    loading, organizeMenuItems, permissionLevels,
    expandedSections, expandedGroups, searchQuery, onSearchChange,
    toggleSection, toggleGroup, handleToggleAll, areAllExpanded,
    getPermissionLevel, hasInheritedPermission, handlePermissionChange,
    setFormData, t
}: RoleProfilePermissionsMatrixProps) {
    return (
        <FormBox title={t('profiles.sections.permissionsMatrix')} icon={<Shield className="h-5 w-5" />}>
            {/* Header with search and expand/collapse */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <SimpleTooltip content={t('profiles.tooltips.permissionsSection')} position="top" maxWidth="300px">
                            <h3 className="text-lg font-medium text-foreground">{t('profiles.permissions')}</h3>
                        </SimpleTooltip>
                        <p className="text-sm text-muted-foreground mt-1">
                            {t('profiles.permissionsDescription')}
                            <span className="text-blue-500">?</span> {t('profiles.inheritedPermissions')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={t('profiles.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => onSearchChange(e.target.value)}
                                className="pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-64"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => onSearchChange('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    aria-label={t('common.clear')}
                                >
                                    �
                                </button>
                            )}
                        </div>
                        {/* Expand/Collapse toggle */}
                        <SimpleTooltip content={areAllExpanded() ? t('common.collapseAll') : t('common.expandAll')} position="top" maxWidth="200px">
                            <button
                                type="button"
                                onClick={handleToggleAll}
                                className="flex items-center justify-center p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
                            >
                                {areAllExpanded() ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                            </button>
                        </SimpleTooltip>
                    </div>
                </div>
            </div>

            {/* Tree */}
            {loading ? (
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            ) : (
                <div className="space-y-2">
                    {organizeMenuItems.map((section) => {
                        const isSectionExpanded = expandedSections.has(section.id);
                        const currentLevel = getPermissionLevel(section.id);

                        return (
                            <div key={section.id} className="space-y-1">
                                {/* Section Header */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center justify-between p-3.5 border border-border rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer"
                                    onClick={() => toggleSection(section.id)}
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                                            {renderIcon(section.icon, "w-5 h-5 text-primary")}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-foreground">
                                                {section.slug ? t(`menu.sections.${section.slug}`, section.title) : section.title}
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">
                                                {t('profiles.labels.groups', { count: section.children.length })}
                                            </span>
                                            <motion.div animate={{ rotate: isSectionExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                                                <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </motion.div>
                                        </div>
                                    </div>
                                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                        {permissionLevels.map((level) => {
                                            const sectionLabel = section.slug ? t(`menu.sections.${section.slug}`, section.title) : section.title;
                                            return (
                                                <button
                                                    key={`section-${section.id}-${level.value}`}
                                                    type="button"
                                                    onClick={() => handlePermissionChange(section.id, level.value as 'FULL' | 'READ' | 'NONE', setFormData)}
                                                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${currentLevel === level.value ? level.color : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}
                                                    aria-label={`${sectionLabel}: ${level.label}`}
                                                >
                                                    {level.value}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </motion.div>

                                {/* Groups */}
                                <motion.div
                                    initial={false}
                                    animate={{ height: isSectionExpanded ? 'auto' : 0, opacity: isSectionExpanded ? 1 : 0 }}
                                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                                    className="overflow-hidden"
                                >
                                    <div className="ml-4 space-y-1">
                                        {section.children.map((group) => {
                                            const isGroupExpanded = expandedGroups.has(group.id);
                                            const groupCurrentLevel = getPermissionLevel(group.id);
                                            const isLeafGroup = group.isLeafGroup || group.children.length === 0;

                                            return (
                                                <div key={group.id} className="space-y-1">
                                                    {/* Group Header */}
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        className={`flex items-center justify-between p-3 border border-border rounded-lg bg-secondary/5 hover:bg-secondary/10 transition-colors ${isLeafGroup ? '' : 'cursor-pointer'}`}
                                                        onClick={() => !isLeafGroup && toggleGroup(group.id)}
                                                    >
                                                        <div className="flex items-center gap-3 flex-1">
                                                            <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center">
                                                                {renderIcon(group.icon, "w-4 h-4 text-secondary")}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-medium text-foreground text-sm">
                                                                    {group.slug ? t(`menu.sections.${group.slug}`, group.title) : group.title}
                                                                </h4>
                                                            </div>
                                                            {!isLeafGroup && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {t('profiles.labels.items', { count: group.children.length })}
                                                                    </span>
                                                                    <motion.div animate={{ rotate: isGroupExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                                                                        <svg className="w-3 h-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                        </svg>
                                                                    </motion.div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                                            {permissionLevels.map((level) => {
                                                                const isInherited = hasInheritedPermission(group.id) && groupCurrentLevel === level.value;
                                                                return (
                                                                    <button
                                                                        key={`group-${group.id}-${level.value}`}
                                                                        type="button"
                                                                        onClick={() => handlePermissionChange(group.id, level.value as 'FULL' | 'READ' | 'NONE', setFormData)}
                                                                        className={`px-2 py-1 text-xs font-medium rounded-full transition-colors relative ${groupCurrentLevel === level.value ? level.color : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}
                                                                        aria-label={`${group.slug ? t(`menu.sections.${group.slug}`, group.title) : group.title}: ${level.label}`}
                                                                    >
                                                                        {level.value}
                                                                        {isInherited && <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" aria-hidden="true" />}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </motion.div>

                                                    {/* Items - non-leaf groups only */}
                                                    {!isLeafGroup && (
                                                        <motion.div
                                                            initial={false}
                                                            animate={{ height: isGroupExpanded ? 'auto' : 0, opacity: isGroupExpanded ? 1 : 0 }}
                                                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="ml-4 space-y-1">
                                                                {group.children.map((item) => {
                                                                    const itemCurrentLevel = getPermissionLevel(item.id);
                                                                    return (
                                                                        <motion.div
                                                                            key={item.id}
                                                                            initial={{ opacity: 0, x: -20 }}
                                                                            animate={{ opacity: 1, x: 0 }}
                                                                            className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors"
                                                                        >
                                                                            <div className="flex items-center gap-3 flex-1">
                                                                                <div className="w-6 h-6 rounded bg-muted/20 flex items-center justify-center">
                                                                                    {renderIcon(item.icon, "w-3 h-3 text-muted-foreground")}
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <h4 className="font-medium text-foreground text-sm">
                                                                                        {item.slug ? t(`menu.sections.${item.slug}`, item.title) : item.title}
                                                                                    </h4>
                                                                                </div>
                                                                            </div>
                                                                            <div className="flex gap-1">
                                                                                {permissionLevels.map((level) => {
                                                                                    const isInherited = hasInheritedPermission(item.id) && itemCurrentLevel === level.value;
                                                                                    return (
                                                                                        <button
                                                                                            key={`item-${item.id}-${level.value}`}
                                                                                            type="button"
                                                                                            onClick={() => handlePermissionChange(item.id, level.value as 'FULL' | 'READ' | 'NONE', setFormData)}
                                                                                            className={`px-2 py-1 text-xs font-medium rounded-full transition-colors relative ${itemCurrentLevel === level.value ? level.color : 'text-muted-foreground hover:text-foreground hover:bg-accent'}`}
                                                                                            aria-label={`${item.slug ? t(`menu.sections.${item.slug}`, item.title) : item.title}: ${level.label}`}
                                                                                        >
                                                                                            {level.value}
                                                                                            {isInherited && <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" aria-hidden="true" />}
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </motion.div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            </div>
                        );
                    })}
                </div>
            )}
        </FormBox>
    );
}
